import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2, ArrowLeft, Sparkles } from 'lucide-react';
import { GoogleSignInButton } from '../components/GoogleSignInButton';
import { SEO } from '../components/SEO';

export default function SignupPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const plan = searchParams.get('plan') || 'free';
  const addon = searchParams.get('addon');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    const result = await register(name, email, password);
    setLoading(false);

    if (result.success) {
      // If user selected a paid plan, redirect to pricing
      if (plan !== 'free' || addon) {
        navigate('/pricing');
      } else {
        navigate('/dashboard');
      }
    } else {
      setError(result.error);
    }
  };

  return (
    <div className="min-h-screen bg-[#060C18] flex" data-testid="signup-page">
      <SEO title="Sign Up" description="Create your free SendPI account. 200 credits/month. PI Check, PI Craft, Chrome Extension, and access to 4.3M precision casts." path="/signup" />
      {/* Left side - Visual */}
      <div className="hidden lg:flex flex-1 items-center justify-center bg-[#0A1222] border-r border-white/5">
        <div className="max-w-md text-center px-12">
          <div className="w-24 h-24 rounded-3xl bg-gradient-to-br from-cyan-500 to-purple-500 mx-auto mb-8 flex items-center justify-center">
            <Sparkles className="w-12 h-12 text-white" />
          </div>
          <h2 className="font-space text-2xl text-white mb-4">
            Predict before you send
          </h2>
          <p className="text-white/45">
            Communicate with clarity, confidence, and intent.
            Predict how your message will land and turn ideas into powerful AI prompts.
          </p>

          {plan !== 'free' && (
            <div className="mt-8 p-4 rounded-xl bg-white/5 border border-white/10">
              <p className="text-white/40 text-xs uppercase tracking-wider mb-2">Selected plan</p>
              <p className="text-white font-medium capitalize">{plan} Plan</p>
              {addon && <p className="text-amber-400 text-sm mt-1">+ Progress Tracking</p>}
            </div>
          )}
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex flex-col justify-center px-8 md:px-16 lg:px-24">
        <div className="max-w-md w-full mx-auto">
          <Link to="/" className="inline-flex items-center gap-2 text-white/55 hover:text-white mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </Link>

          <div className="mb-8">
            <Link to="/" className="inline-block mb-6">
              <img src="/sendpi-icon.png" alt="SendPI" className="h-9 w-auto inline-block mr-2 align-middle" />
              <span className="font-space text-2xl font-bold text-white align-middle">Send<span className="text-[var(--pi-cyan)]">PI</span></span>
            </Link>
            <h1 className="font-space text-3xl text-white mb-2">Create your account</h1>
            <p className="text-white/45">Get started with SendPI in seconds</p>
          </div>

          {error && (
            <div className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm" data-testid="signup-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-white/70">Full name</Label>
              <Input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="John Doe"
                required
                className="bg-[#0E1830] border-white/10 text-white placeholder:text-white/25 focus:border-cyan-500 focus:ring-cyan-500/20"
                data-testid="signup-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-white/70">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                className="bg-[#0E1830] border-white/10 text-white placeholder:text-white/25 focus:border-cyan-500 focus:ring-cyan-500/20"
                data-testid="signup-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-white/70">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                minLength={6}
                className="bg-[#0E1830] border-white/10 text-white placeholder:text-white/25 focus:border-cyan-500 focus:ring-cyan-500/20"
                data-testid="signup-password-input"
              />
              <p className="text-white/40 text-xs">Must be at least 6 characters</p>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full shimmer-btn text-white rounded-xl py-6 border-0"
              data-testid="signup-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating account...
                </>
              ) : (
                'Create account'
              )}
            </Button>
          </form>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-white/10" /></div>
            <div className="relative flex justify-center text-xs"><span className="px-3 bg-[#060C18] text-white/40">or</span></div>
          </div>

          <GoogleSignInButton label="Sign up with Google" />

          <p className="mt-6 text-white/40 text-xs text-center">
            By signing up, you agree to our{' '}
            <Link to="/terms" className="text-white/55 hover:text-white underline">Terms of Service</Link>
            {' '}and{' '}
            <Link to="/privacy" className="text-white/55 hover:text-white underline">Privacy Policy</Link>
          </p>

          <div className="mt-8 text-center">
            <p className="text-white/55">
              Already have an account?{' '}
              <Link to="/login" className="text-cyan-400 hover:text-cyan-300">
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
